<template>
  <ValidationObserver rules="required" v-slot="{ handleSubmit }">
    <RightSideBar
      v-if="isOpen"
      @submit="handleSubmit(onSubmit)"
      @close="onClose()"
      submit="Submit"
      :loading="isLoading"
      :disabled-button="isLoading"
      button-class="bg-dynamicBackBtn text-white"
      width="520px"
    >
      <template v-slot:title>
        <div class="flex justify-start items-center">
          <BackButton @onClick="onClose()" variant="secondary" />
          <span class="text-darkPurple font-bold text-lg leading-5 p-0 -ml-2">
            {{ title }}
          </span>
        </div>
      </template>
      <template>
        <EmployeeDetails
          :employee-info="employeeInfo"
          title="Employee Details"
        />
      </template>
      <template>
        <div class="w-full flex flex-col justify-start items-start gap-4">
          <div class="w-full flex flex-col justify-start gap-2">
            <div class="flex">
              <p class="text-jet text-sm mr-5">
                Effective Date
              </p>
            </div>
            <validation-provider class="w-full" name="Effective date" :rules="{required: true}" v-slot="{ errors }">
              <div :class="errors[0] ? 'border border-desire rounded-md' : 'date-flex'">
                <datepicker
                  placeholder="--Start Date--"
                  input-class="date-input"
                  style="width:100%;"
                  :rules="['required']"
                  v-model="payload.effectiveDate"
                />
              </div>
              <small class="text-desire">{{ errors[0] }}</small>
            </validation-provider>
          </div>
          <div class="w-full text-darkPurple">
            <c-select
              placeholder="--Select--"
              :options="locationOptions"
              label="Select Location"
              variant="w-full"
              :rules="['required']"
              v-model="payload.newOfficeId"
              @input="onGetPayBandByOrgParams"
            />
          </div>
          <div class="w-full">
            <c-select
              placeholder="--Select--"
              :options="designationOptions"
              label="Select Designation"
              variant="w-full"
              :rules="['required']"
              v-model="payload.newDesignationId"
              @input="onChangeDesignation"
            />
          </div>
          <div class="w-full text-darkPurple">
            <div class="text-sm mb-2 text-jet">
              Function
            </div>
            <div class="payInfo payInfo_fakeInput text-center">
              {{ orgFunction.name || "--Generated--" }}
            </div>
          </div>
          <div class="w-full text-darkPurple">
            <div class="text-sm mb-2 text-jet">
              Pay Band/Range
            </div>
            <div class="payInfo payInfo_fakeInput text-center">
              <span v-if="isFetchingPayBand">Please wait...</span>
              <template v-else>
                <span v-if="Object.keys(payload.payband).length && Object.keys(payload.newLevel).length">
                  {{ convertToCurrency(payload.payband.rangeMinimum) }} -
                  {{ convertToCurrency(payload.payband.rangeMaximum) }}
                </span>
                <span v-else>--Generated--</span>
              </template>
            </div>
          </div>
          <div class="w-full text-darkPurple">
            <span class="text-sm mb-2 text-jet">New Manager</span>
            <div class="payInfo payInfo_fakeInput text-center">
              <span v-if="newManager.fname">{{ newManager.fname }} {{ newManager.lname }}</span>
              <span v-else-if="newManager.name">{{ newManager.name }}</span>
              <span v-else>--Generated--</span>
            </div>
          </div>
          <div class="w-full text-darkPurple">
            <span class="font-normal text-sm">
              Is this redeployment eligible for payroll review?
            </span>
            <validation-provider class="w-full" name="Payroll review eligibility" :rules="{required: true}" v-slot="{ errors }">
              <div class="w-44">
                <c-radio
                  v-model="payload.payrollReview"
                  space-between="mr-2 ml-2 text-jet text-base font-normal"
                  :options="[{ name: 'Yes', value: 'true' }, { name: 'No', value: 'false' }]"
                />
              </div>
              <small class="text-desire">{{ errors[0] }}</small>
            </validation-provider>
          </div>
          <template v-if="payload.payrollReview === 'true'">
            <div class="w-full text-darkPurple">
              <div class="text-sm mb-2 text-jet">
                Current Gross Annual Salary(₦)
              </div>
              <div class="payInfo payInfo_fakeInput text-center">
                {{ convertToCurrency(employeeInfo.grossPay) }}
              </div>
            </div>
            <template>
              <div class="w-full flex flex-col justify-start gap-2">
                <p class="text-sm">New Gross Annual Salary(₦)</p>
                <div class="flex flex-col items-center gap-5 w-full border border-romanSilver rounded-md p-3">
                  <c-radio
                    v-model="increaseBy"
                    class="_delegation-radio"
                    space-between="mr-2 ml-2"
                    :rules="['required']"
                    :options="[
                      { name: 'Amount', value: 'amount' },
                      { name: '% Increase', value: 'percent_increase' },
                      { name: '% Decrease', value: 'percent_decrease' },
                    ]"
                  />
                  <template>
                    <div class="flex flex-col justify-start gap-5">
                      <div class="flex flex-row justify-between items-center gap-5">
                        <c-text
                          :disabled="!(increaseBy === 'amount')"
                          v-model="reviseByAmount"
                          placeholder="--Enter--"
                          label="Amount"
                          type="number"
                        />
                        <c-text
                          :disabled="increaseBy === 'amount'"
                          v-model="reviseByPercent"
                          placeholder="--Enter--"
                          label="Percentage"
                          type="number"
                          max="100"
                        />
                      </div>
                      <div class="flex flex-col justify-start items-start gap-1">
                        <div class="w-full text-darkPurple">
                          <p class="text-sm mb-2 text-jet">New Gross Annual Salary(₦)</p>
                          <div class="payInfo payInfo_fakeInput text-center">
                            {{ computedNewAnnualGross(employeeInfo.grossPay) }}
                          </div>
                        </div>
                        <template v-if="payload.payband && Object.keys(payload.payband).length && payload.newGrossAnnualSalary">
                          <small class=" text-flame" v-if="payload.newGrossAnnualSalary < payload.payband.rangeMinimum">
                            <span>* New annual gross salary is less than pay range</span>
                          </small>
                          <small class=" text-flame" v-if="payload.newGrossAnnualSalary > payload.payband.rangeMaximum">
                            <span>* New annual gross salary is more than pay range</span>
                          </small>
                        </template>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </template>
            <div class="w-full text-darkPurple" v-feature-request>
              <span class="font-normal text-sm">
                Is this redeployment eligible for Relocation Allowance?
              </span>
              <div class="w-44">
                <c-radio
                  :rules="['required']"
                  v-model="payload.relocationAllowance"
                  space-between="mr-2 ml-2 text-jet text-base font-normal"
                  :options="[{ name: 'Yes', value: 'true' }, { name: 'No', value: 'false'}]"
                />
              </div>
            </div>
          </template>
        </div>
      </template>
      <template>
        <Modal v-if="isPaybandWarning" class="">
          <Card class=" p-2 px-3 flex flex-col gap-3 max-w-md">
            <template>
              <div class="border-b border-romanSilver">
                <p class="text-jet font-bold text-base">Action Required!</p>
              </div>
              <div class="w-full flex flex-col justify-start gap-2">
                <div class="relative mb-5 w-full">
                  <div class="w-full px-5 py-2 flex self-center mb-2 rounded-md bg-yellow-100 border-carrotOrange border">
                    <div class="flex justify-start items-center gap-2">
                      <Icon icon-name="icon-info" size="xs" class="text-carrotOrange" />
                      <p class="text-carrotOrange w-full rounded-md text-center text-sm">
                        <template v-if="payload.newGrossAnnualSalary">
                          <span v-if="payload.newGrossAnnualSalary < payload.payband.rangeMinimum">
                            New annual gross salary is less than pay range
                          </span>
                          <span v-if="payload.newGrossAnnualSalary > payload.payband.rangeMaximum">
                            New annual gross salary is more than pay range
                          </span>
                        </template>
                        <template v-else>
                          <span v-if="employeeInfo.grossPay < payload.payband.rangeMinimum">
                            New annual gross salary is less than pay range
                          </span>
                          <span v-if="employeeInfo.grossPay > payload.payband.rangeMaximum">
                            New annual gross salary is more than pay range
                          </span>
                        </template>
                      </p>
                    </div>
                  </div>
                  <div class="w-full flex flex-col justify-start items-start gap-1">
                    <div class="flex justify-start items-center gap-2">
                      <p class="font-semibold text-sm text-jet">Salary Range:</p>
                      <p class="font-bold text-sm leading-5 text-jet">
                        <span v-if="payload.payband">
                          {{ convertToCurrency(payload.payband.rangeMinimum) }}
                            -
                          {{ convertToCurrency(payload.payband.rangeMaximum) }}
                        </span>
                        <span v-else>-</span>
                      </p>
                    </div>
                    <div class="flex justify-start items-center gap-2">
                      <p class="font-semibold text-sm text-jet">Current Annual Gross:</p>
                      <p class="font-bold text-sm text-jet">
                        <span v-if="!payload.currGrossAnnualSalary">{{ convertToCurrency(employeeInfo.grossPay) }}</span>
                        <span v-else>{{ convertToCurrency(payload.currGrossAnnualSalary) }}</span>
                      </p>
                    </div>
                    <div class="flex justify-start items-center gap-2">
                      <p class="font-semibold text-sm text-jet">New Annual Gross:</p>
                      <p class="font-bold text-sm text-jet">
                        <span v-if="!payload.newGrossAnnualSalary">{{ convertToCurrency(employeeInfo.grossPay) }}</span>
                        <span v-else>{{ convertToCurrency(payload.newGrossAnnualSalary) }}</span>
                      </p>
                    </div>
                  </div>
                </div>
                <p>Do you wish to continue?</p>
                <div class="flex justify-end items-center gap-2">
                  <Button
                    label="Cancel"
                    class="button-class button-class-secondary"
                    @onClick="isPaybandWarning = false"
                    :disabled="isLoading"
                  />
                  <Button
                    class="button-class button-class-pimary btn-bg"
                    label="Continue"
                    @onClick="onSubmit"
                    :disabled="isLoading"
                  />
                </div>
              </div>
            </template>
          </Card>
        </Modal>
      </template>
    </RightSideBar>
  </ValidationObserver>
</template>

<script>
  import * as _ from "lodash"
  import { format } from "date-fns"
  import "vue-select/dist/vue-select.css"
  import Datepicker from "vuejs-datepicker"
  import { ValidationObserver, ValidationProvider } from "vee-validate"
  import BackButton from "@scelloo/cloudenly-ui/src/components/back-button"
  import CSelect from "@scelloo/cloudenly-ui/src/components/select"
  import Button from "@scelloo/cloudenly-ui/src/components/button"
  import CRadio from "@scelloo/cloudenly-ui/src/components/radio"
  import CText from "@scelloo/cloudenly-ui/src/components/text"
  import Card from "@scelloo/cloudenly-ui/src/components/card"
  import RightSideBar from "@/components/RightSideBar"
  import Modal from "@/components/Modal"

  export default {
    components: {
      ValidationObserver,
      ValidationProvider,
      Datepicker,
      BackButton,
      RightSideBar,
      CSelect,
      Button,
      CRadio,
      CText,
      Card,
      Modal,
      EmployeeDetails: () => import('./details'),
    },
    props: {
      title: {
        type: String,
        default: 'Redeployment'
      },
    },
    data(){
      return {
        isLoading: false,
        isFetchingPayBand: false,
        isPaybandWarning: false,
        isOpen: false,
        employeeInfo: {},
        reviseByAmount: '',
        reviseByPercent: '',
        increaseBy: 'amount',
        designationOptions: [],
        locationOptions: [],
        employeesOptions: [],
        orgFunctionOptions: [],
        newManager: {},
        orgFunction: {},
        payload: {
          payrollReview: '',
          effectiveDate: '',
          newDesignationId: '',
          newGrossAnnualSalary: '',
          newOfficeId: '',
          increaseBy: '',
          reportingTo: '',
          relocationAllowance: 'false',
          functionId: '',
          orgFunction: '',
          newLevel: {},
          payband: {}
        },
      }
    },
    methods: {
      toggle({ employeeInfo, redeployment }){
        this.employeeInfo = employeeInfo
        this.isOpen = true

        if (redeployment) {
          this.payload = {...this.payload, ...redeployment}
          this.onGetPayBandByOrgParams()
        }
      },
      onClose(){
        Object.assign(this.$data.payload, this.$options.data().payload)
        this.employeeInfo = {}
        this.redeployment = {}
        this.newManager = {}
        this.queryString = null
        this.$emit('close')
        this.isOpen = false
      },
      async onChangeDesignation(newDesignationId){
        const designation = this.designationOptions.find(i => (i.id === newDesignationId))
        this.orgFunction = this.orgFunctionOptions.find(f => f.id === designation.functionId)
        const reportingTo = this.designationOptions.find(v => v.id === designation.reportingTo)
        this.newManager = reportingTo.hasUser || reportingTo.reportsTo || {}

        this.payload = {
          ...this.payload,
          newLevel: designation.level,
          functionId: designation.functionId,
          newManagerId: designation.reportingTo,
          reportingTo: designation.reportingTo

        }
        this.onGetPayBandByOrgParams()
      },
      async onGetPayBandByOrgParams(){
        this.isFetchingPayBand = true
        await this.$_getPaygradeByOrgParameters({
          orgId: this.$orgId,
          levelId: this.payload.newLevel.id,
          designationId: this.payload.newDesignationId,
          functionId: this.payload.functionId,
          locationId: this.payload.newOfficeId
        }).then(({ data }) => {
          this.payload.payband = data.data || {}
          this.isFetchingPayBand = false
        }).catch((error) => {
          this.$toasted.error(error.message, {duration: 3000})
          this.isFetchingPayBand = false
          this.payload.payband = {}
        })
      },
      computedNewAnnualGross(value){
        /* eslint-disable no-param-reassign */
        if(this.increaseBy === 'amount'){
          this.reviseByPercent = ''
          value = Number(value) + Number(this.reviseByAmount)
        } else if(this.increaseBy === 'percent_increase'){
          this.reviseByAmount = ''
          value = Math.fround(((Math.abs(this.reviseByPercent) / 100) * Number(value)) + Number(value))
        } else if(this.increaseBy === 'percent_decrease'){
          this.reviseByAmount = ''
          value = Math.fround(Number(value) - ((Math.abs(this.reviseByPercent) / 100) * Number(value)))
        } else value = Number(value)
        this.payload.newGrossAnnualSalary = value
        return this.convertToCurrency(value)
      },
      onSubmit(){
        const {rangeMinimum, rangeMaximum} = this.payload.payband
        const newGross = this.payload.newGrossAnnualSalary
        const isInRange = _.inRange(newGross, rangeMinimum, rangeMaximum)
        const isPayband = _.size(_.keys(this.payload.payband))
        if(!isInRange && !!isPayband && !this.isPaybandWarning){
          this.isPaybandWarning = true
          return undefined
        }

        this.isLoading = true

        const payload = {
          ...this.payload,
          orgId: this.$orgId,
          userId: this.employeeInfo.userId,
          increaseBy: this.increaseBy === 'amount' ? 'amount' : 'percentage',
          payRangeStart: this.payload.payband.rangeMinimum || 0,
          payRangeEnd: this.payload.payband.rangeMaximum || 0,
          newLevelId: this.payload.newLevel.id,
          initiatedBy: this.$AuthUser.id,
          reportingTo: this.payload.reportingTo,
          functionId: this.payload.functionId,
          payrollReview: JSON.parse(this.payload.payrollReview),
          currGrossAnnualSalary: this.employeeInfo.grossPay || 0,
          relocationAllowance: JSON.parse(this.payload.relocationAllowance),
          effectiveDate: format(new Date(this.payload.effectiveDate), "yyyy-MM-dd"),
        }

        const query = this.payload.id ? '&update=true' : ''
        return this.$_employeeActionRedeployeEmployee({ payload, query }).then(({ data }) => {
          const message = "Employee promotion request successfully sent for approval"
          this.$toasted.success(message, { duration: 3000 })
          this.isPaybandWarning = false
          this.isLoading = false
          this.$emit('redeployed', data)
          this.$emit('close', data)
          this.employeeInfo = {}
          this.isOpen = false
        }).catch((error) => {
          this.$toasted.error(error.message, { duration: 3000 })
          this.isPaybandWarning = false
          this.isLoading = false
        })
      },
      async onSearchEmployees(queryString, items) {
        clearTimeout(this.timeout);
        if (!queryString) {
          const qs = `&perPage=${items}`
          await this.$_getAllEmployeesWithQueries(qs).then(({ data }) => {
            this.employeesOptions = _.filter(data.data.employees,
              employee => employee.userId !== this.employeeInfo.userId
            )
          })
        }
        this.timeout = setTimeout(async () => {
          const qs = `&search=${queryString}&perPage=${items}`
          await this.$_getAllEmployeesWithQueries(qs).then(({ data }) => {
            this.employeesOptions = _.filter(data.data.employees,
              employee => employee.userId !== this.employeeInfo.userId
            )
          })
        }, 500)
      },
      async getOrgDesignations(){
        await this.$_getFunctionDesignations().then(({ data }) => {
          this.orgFunctionOptions = data.functions
          const designations = data.functions.map(d => d.designations)?.flat()
          this.designationOptions = designations.map((element) => ({
            ...element, disabled: (!!element.isSupervisory && !!element.hasUser)
          }))
        })
      },
      async getOrgOfficeLocations(){
        await this.$_getHumanarLocations().then(({ data }) => {
          this.locationOptions = data.outlets?.map((outlet) => ({
            ...outlet, value: outlet.id
          }))
        })
      },
      async getOrgEmployeesWithQueries(){
        await this.$_getAllEmployeesWithQueries('').then(({ data }) => {
          this.employeesOptions = data.data.employees?.map((employee) => ({
            ...employee, id: employee.userId,
            name: `${employee.fname} ${employee.lname}`,
          }))
        })
      },
    },
    async created(){
      this.isLoading = true
      await this.getOrgDesignations()
      await this.getOrgOfficeLocations()
      await this.getOrgEmployeesWithQueries()
      await this.onSearchEmployees('', 10)
      this.isLoading = false
    },
  }
</script>

<style lang="scss">
.rightSidebar{
  width: 540px !important;
}
.payInfo {
  display: flex;
  flex-direction: row;
  align-items: center;
  border-style: solid;
  border-width: 1px;
  border-radius: 5px;
  &_fakeInput {
    background: #f7f7ff;
    border-color: #878e99;
    padding: 10px 15px;
    height: 40px;
  }
}
</style>
<style scoped>
._delegation-radio {
  width: 100% !important;
  display: flex !important;
  justify-content: space-between !important;
  font-size: 16px !important;
  /* font-weight: 600 !important; */
  color: #333333 !important;
}
._text-10 {
  font: 10px !important;
}
</style>

<style scoped>
.button_class {
  padding: 10px 40px !important;
  font-weight: 600;
  display: flex;
  justify-self: end;
}
.button-class-pimary {
  font-weight: 600 !important;
  font-size: 16px !important;
  padding: 10px 40px !important;
}
.button-class-secondary {
  color: #321c3b !important;
  background: white !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  padding: 10px 40px !important;
}
.alert-style {
  position: relative;
  right: 0; top: -5px;
  box-shadow: none;
  background: #FFFFFF;
  color: #E99323;
  font-size: 16px;
  font-weight: bold;
  border: 1px solid rgb(254 202 202);
}

.btn-bg {
    background-color: var(--dynamic-Background) !important;
 }
</style>

